import { useFormContext } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import { DIMENSIONALITY_VALUES } from 'constants/businessTool';
import { useGetRecalculationsTotalExpanses } from 'services/calculations/expenses/hooks/useGetRecalculationsByCategory';

import { IPeriodsTableRow } from 'components/BusinessToolForm/constants/periods';
import {
  netIncomePrefix,
  netSalesPrefix,
  operatingExpensesPrefix,
} from 'components/BusinessToolForm/Sections/FinancialPlan/ProfitAndLossProjection/utils/form';
import { convertToOpposite } from 'components/BusinessToolForm/utils/helpers';

import { concatWithDot } from 'helpers/string';
import ToastService from 'helpers/ToastService';

import { GLOBAL_UNIT_NAME } from '../../Global/form';
import { LOAN_REPAYMENT_SCHEDULE_NAMES } from '../CashFlowElements/Sections/LoanRepaymentScheduleForm/utils/form';
import { loanRepaymentSchedulePrefix } from '../CashFlowElements/utils/form';
import {
  MONTHLY_EXPENSES_FORM_CHUNKS,
  MONTHLY_EXPENSES_FORM_NAMES,
  MONTHLY_EXPENSES_FORM_TYPES,
} from '../PlannedExpenses/MonthlyExpensesForm/utils/form';
import { TOTAL_EXPENSES_FORM_NAMES } from '../PlannedExpenses/TotalExpensesForm/utils/form';
import { monthlyExpensesPrefix, totalExpensesPrefix } from '../PlannedExpenses/utils/form';
import { NET_INCOME_NAMES } from '../ProfitAndLossProjection/components/FormSections/NetIncome/utils/form';
import { NET_SALES_NAMES } from '../ProfitAndLossProjection/components/FormSections/NetSalesForm/utils/form';
import { OPERATING_EXPENSES_NAMES } from '../ProfitAndLossProjection/components/FormSections/OperatingExpensesForm/utils/form';
import { OPERATING_ACTIVITIES_NAMES } from '../ProjectedCashFlow/DirectForm/DirectFormComponents/Sections/OperatingActivities/utils/form';
import { OTHER_ITEMS_NAMES } from '../ProjectedCashFlow/InDirectForm/InDirectFormComponents/Sections/OtherItemsForm/utils/form';
import { operatingActivitiesPrefix, otherItemsPrefix } from '../ProjectedCashFlow/utils/form';

export const useTotalExpensesRecalculation = () => {
  const { getValues, setValue } = useFormContext();
  const { id: businessPlanId = '' } = useParams();
  const { mutateAsync, isPending } = useGetRecalculationsTotalExpanses();

  const fieldPrefixes = {
    costOfSales: concatWithDot(netSalesPrefix, NET_SALES_NAMES.COST_OF_SALES),
    financialExpenses: concatWithDot(netIncomePrefix, NET_INCOME_NAMES.FINANCIAL_EXPENSES),
    generalAndAdministrative: concatWithDot(
      operatingExpensesPrefix,
      OPERATING_EXPENSES_NAMES.GENERAL_AND_ADMINISTRATIVE,
    ),
    nonOperatingExpenses: concatWithDot(netIncomePrefix, NET_INCOME_NAMES.NON_OPERATING_EXPENSES),
    // TODO delete if client agreed
    // lossOnSale: concatWithDot(investingActivitiesPrefix, INVESTING_ACTIVITIES_NAMES.LOSS_ON_SALE_OF_ASSETS),
    otherOperatingExpenses: concatWithDot(operatingExpensesPrefix, OPERATING_EXPENSES_NAMES.OTHER_OPERATING_EXPENSES),
    researchAndDevelopment: concatWithDot(operatingExpensesPrefix, OPERATING_EXPENSES_NAMES.R_AND_D),
    salesAndMarketing: concatWithDot(operatingExpensesPrefix, OPERATING_EXPENSES_NAMES.SALES_AND_MARKETING),
    loanPaymentTable: concatWithDot(loanRepaymentSchedulePrefix, LOAN_REPAYMENT_SCHEDULE_NAMES.TABLE),
    expensesTable: concatWithDot(totalExpensesPrefix, TOTAL_EXPENSES_FORM_NAMES.EXPENSES_TABLE),
    expensesTableTotal: concatWithDot(totalExpensesPrefix, TOTAL_EXPENSES_FORM_NAMES.EXPENSES_TABLE_TOTAL),
    interestPaidField: concatWithDot(operatingActivitiesPrefix, OPERATING_ACTIVITIES_NAMES.INTEREST_PAID),
    otherOperatingExpansesField: concatWithDot(
      operatingActivitiesPrefix,
      OPERATING_ACTIVITIES_NAMES.CASH_PAID_OTHER_OPERATING_EXPENSES,
    ),
    interestPaidIndirectField: concatWithDot(otherItemsPrefix, OTHER_ITEMS_NAMES.INTEREST_PAID),
  };

  const updateFormFields = (companyExpenseType: string, periodValues: any) => {
    const fieldMapping: Record<string, string> = {
      [MONTHLY_EXPENSES_FORM_TYPES.COST_OF_SALES_PRODUCTS]: fieldPrefixes.costOfSales,
      [MONTHLY_EXPENSES_FORM_TYPES.FINANCIAL_EXPENSES]: fieldPrefixes.financialExpenses,
      [MONTHLY_EXPENSES_FORM_TYPES.GENERAL_AND_ADMINISTRATIVE]: fieldPrefixes.generalAndAdministrative,
      [MONTHLY_EXPENSES_FORM_TYPES.NON_OPERATING_EXPENSES]: fieldPrefixes.nonOperatingExpenses,
      [MONTHLY_EXPENSES_FORM_TYPES.OTHER_OPERATING_EXPENSES]: fieldPrefixes.otherOperatingExpenses,
      [MONTHLY_EXPENSES_FORM_TYPES.RESEARCH_AND_DEVELOPMENT]: fieldPrefixes.researchAndDevelopment,
      [MONTHLY_EXPENSES_FORM_TYPES.SALES_AND_MARKETING]: fieldPrefixes.salesAndMarketing,
    };

    if (fieldMapping[companyExpenseType]) {
      setValue(fieldMapping[companyExpenseType], periodValues);
    }

    if (companyExpenseType === MONTHLY_EXPENSES_FORM_TYPES.FINANCIAL_EXPENSES) {
      const loanValues = getValues(fieldPrefixes.loanPaymentTable);
      setValue(fieldPrefixes.loanPaymentTable, [loanValues[0], periodValues]);
      setValue(fieldPrefixes.interestPaidField, convertToOpposite(periodValues));
      setValue(fieldPrefixes.interestPaidIndirectField, convertToOpposite(periodValues));
    }

    if (companyExpenseType === MONTHLY_EXPENSES_FORM_TYPES.OTHER_OPERATING_EXPENSES) {
      setValue(fieldPrefixes.otherOperatingExpansesField, convertToOpposite(periodValues));
    }
  };

  const recalculateHandler = async (rowIndex: number, fieldName: string, data: IPeriodsTableRow, recalculateLoan: boolean = false) => {
    if (!businessPlanId) return;

    const tableValues = getValues(fieldPrefixes.expensesTable);
    const productionData = data;
    const unit = getValues(GLOBAL_UNIT_NAME) ?? DIMENSIONALITY_VALUES.SINGLE;
    const productIndex = fieldName.split('.').at(-1) ?? '';
    const companyExpenseType = Object.values(MONTHLY_EXPENSES_FORM_TYPES).at(
      rowIndex - 1,
    ) as keyof typeof MONTHLY_EXPENSES_FORM_TYPES;
    const totalGeneralExpense = getValues(fieldPrefixes.expensesTableTotal);
    const monthlyExpensesName = Object.values(MONTHLY_EXPENSES_FORM_CHUNKS).at(
      rowIndex - 1,
    ) as keyof typeof MONTHLY_EXPENSES_FORM_NAMES;

    const plannedMonthlyExpenses = Number(
      getValues(concatWithDot(monthlyExpensesPrefix, MONTHLY_EXPENSES_FORM_NAMES[monthlyExpensesName].EXPENSES)),
    );
    const annualExpenseGrowthRate = Number(
      getValues(concatWithDot(monthlyExpensesPrefix, MONTHLY_EXPENSES_FORM_NAMES[monthlyExpensesName].GROWTH_RATE)),
    );
    const costOfSales = getValues(fieldPrefixes.costOfSales);
    const variableCostOfSales = tableValues[0];
    const fixedCostOfSales =
      companyExpenseType === MONTHLY_EXPENSES_FORM_TYPES.COST_OF_SALES_PRODUCTS
        ? tableValues[1]
        : recalculateLoan ? productionData : tableValues[rowIndex];

    const generalExpenseData = {
      productId: '', // TODO: delete later
      productIndex,
      companyExpenseType,
      plannedMonthlyExpenses,
      annualExpenseGrowthRate,
      fixedCostOfSales,
      variableCostOfSales,
      totalGeneralExpense,
      generalExpenseResponseData: {
        type: companyExpenseType,
        shareExpense: 0,
        calculatedResponse:
          companyExpenseType === MONTHLY_EXPENSES_FORM_TYPES.COST_OF_SALES_PRODUCTS ? costOfSales : recalculateLoan ? tableValues[rowIndex] : productionData,
      },
    };

    const params = {
      unit,
      businessPlanId,
      generalExpenseData: { generalExpenseData },
    };

    try {
      const { generalExpenseResponseData, fixedCostOfSales, totalGeneralExpense } = await mutateAsync(params);
      const periodValues = generalExpenseResponseData.at(0)?.calculatedResponse;

      const updatedTable = tableValues.toSpliced(
        rowIndex,
        1,
        companyExpenseType === MONTHLY_EXPENSES_FORM_TYPES.COST_OF_SALES_PRODUCTS ? fixedCostOfSales : periodValues,
      );
      setValue(fieldPrefixes.expensesTable, updatedTable);
      updateFormFields(companyExpenseType, periodValues);
      setValue(fieldPrefixes.expensesTableTotal, totalGeneralExpense);
    } catch (error) {
      if (error instanceof Error) {
        ToastService.showError(error.message);
      }
    }
  };

  return { recalculateHandler, isPending };
};
